import lodashCloneDeep from "lodash/cloneDeep";
import {
  getDefaultMarkerOptions,
  getDefaultPolygonOptions,
} from "@/common/maps/styles";
import {
  addSource,
  removeSource,
  removeAllDataSources,
} from "@/common/maps/sources";
import {
  addLayer,
  removeLayer,
  removeAllDataLayers,
} from "@/common/maps/layers";
import { addCluster, removeCluster } from "@/common/maps/clusters";
import {
  mapGeohashedAggregation,
  removeGeohashAggregation,
} from "@/common/maps/aggregation";
import { layerClickEventHandlerOn } from "@/common/maps/eventHandler";
import { LAYER_ID_ACETATE } from "@/common/constants/maps";

export { _addMapData as addMapData };
export { _mapMarkerData as mapMarkerData };
export { _mapPolygonData as mapPolygonData };
export { _removeMapData as removeMapData };
export { _addTileData as addTileData };
export { _removeTileData as removeTileData };
export { _addAcetate as addAcetate };
export { _fakeMapFeature as fakeMapFeature };

const CLUSTER_RADIUS = 50;
const MAP_EXTENT = 8192; // See here for logic on this number: https://github.com/mapbox/mapbox-gl-js/blob/master/src/data/extent.js

const _addMapData = (options) => {
  /*
   * OPTIONS
   * id:  source ID
   * aggData:  endpoint for heatmap / geohash agg
   * data: endpoint for markers
   * mapClickEvent: function when markers are clicked
   * heatMapOptions:  options for heatmap
   * geohashOptions:  options for Geohash agg
   * markerOptions:  options for markers
   * noCluster:  don't cluster the data
   * container: map container id
   * map: map object
   * */

  // temporarily save the map and mapclickevents so we don't have to clone those and then add them back
  const MAP = options.map ? options.map : null;
  const MCE = options.mapClickEvent ? options.mapClickEvent : null;
  options.map = null;
  options.mapClickEvent = null;

  let dataOptions = lodashCloneDeep(options);
  dataOptions.map = MAP;
  dataOptions.mapClickEvent = MCE;

  const AGG_DATA = options.data || options.aggData;
  let aggOptions = lodashCloneDeep(options);
  aggOptions.data = AGG_DATA;
  aggOptions.map = MAP;
  aggOptions.mapClickEvent = MCE;

  aggOptions.heatmap = !!options.heatmap;

  options.map = MAP;
  options.mapClickEvent = MCE;

  if (options.hasOwnProperty("aggData")) {
    mapGeohashedAggregation(aggOptions);
  } else {
    if (!options.hasOwnProperty("markerOptions")) {
      dataOptions.markerOptions = getDefaultMarkerOptions({ id: options.id });
    }
    _mapMarkerData(dataOptions);
  }
};

const _mapMarkerData = (options) => {
  if (isAcetateAdded(options.map)) {
    __mapMarkerData(options);
  } else {
    setTimeout(() => {
      _mapMarkerData(options);
    }, 1000);
  }
};

const __mapMarkerData = (options) => {
  /*
   * OPTIONS
   * id:  source ID
   * data: endpoint for markers
   * mapClickEvent: function when markers are clicked
   * clusterMapClickEvent: function when a mapped cluster is clicked
   * markerOptions:  options for markers
   * noCluster:  don't cluster the data
   * map:  map object
   * */

  let markerSource = {
    type: "geojson",
    data: options.data,
  };

  if (!options.noCluster) {
    markerSource["cluster"] = true;
    markerSource["clusterRadius"] = CLUSTER_RADIUS; // Radius of each cluster when clustering points (defaults to 50)
  }

  if (!options.markerOptions) {
    options.markerOptions = getDefaultMarkerOptions({ id: options.id });
  }

  addSource(options.map, options.id, markerSource);
  addLayer(options.map, options.markerOptions.markerLayerOptions);

  if (!options.noCluster) {
    let layerSource = options.map.getSource(options.id);

    let scale = MAP_EXTENT / layerSource.tileSize;
    let clusterOptions = {
      radius: CLUSTER_RADIUS * scale,
      extent: MAP_EXTENT,
      maxZoom: layerSource.maxzoom - 1,
      clusterMapClickEvent: options.clusterMapClickEvent,
    };
    addCluster(options.map, options.id, options.data, clusterOptions);
  }

  if (options.mapClickEvent) {
    const MAP_CLICK_EVENT = options.mapClickEvent;
    layerClickEventHandlerOn(
      options.map,
      options.markerOptions.markerLayerOptions.id,
      MAP_CLICK_EVENT
    );
    options.markerOptions.markerLayerOptions.mapClickEvent = MAP_CLICK_EVENT;
  }
  if (options.markerOptions.markerClusterLayerOptions) {
    addLayer(options.map, options.markerOptions.markerClusterLayerOptions);
  }
};

const _mapPolygonData = (options) => {
  if (isAcetateAdded(options.map)) {
    __mapPolygonData(options);
  } else {
    setTimeout(() => {
      __mapPolygonData(options);
    }, 1000);
  }
};

const __mapPolygonData = (options) => {
  /*
   * OPTIONS
   * id:  source ID
   * data: endpoint for markers
   * mapClickEvent: function when markers are clicked
   * polygonOptions:  options for polygons
   * map:  map object
   * */

  let polygonSource = {
    type: "geojson",
    data: options.data,
  };

  if (!options.polygonOptions) {
    options.polygonOptions = getDefaultPolygonOptions(options.id);
  }

  let beforeLayer = null;
  if (options.beforeLayer) {
    beforeLayer = options.beforeLayer;
  }

  addSource(options.map, options.id, polygonSource);
  addLayer(
    options.map,
    options.polygonOptions.polygonLayerOptions,
    beforeLayer
  );

  if (options.mapClickEvent) {
    const MAP_CLICK_EVENT = options.mapClickEvent;
    layerClickEventHandlerOn(
      options.map,
      options.polygonOptions.polygonLayerOptions.id,
      MAP_CLICK_EVENT
    );
    options.polygonOptions.polygonLayerOptions.mapClickEvent = MAP_CLICK_EVENT;
  }
};

const _removeMapData = (map, layerId) => {
  removeAllDataLayers(map, layerId);
  removeAllDataSources(map, layerId);
  removeCluster(map, layerId);
  removeGeohashAggregation(map, layerId);
};

const _addTileData = (options) => {
  addSource(options.map, options.id, options.source);

  for (let i = 0; i < options.layerOptions.length; i++) {
    addLayer(
      options.map,
      options.layerOptions[i],
      options.layerOptions[i].before
    );
  }

  if (options.mapClickEvent) {
    const MAP_CLICK_EVENT = options.mapClickEvent;
    for (let j = 0; j < options.layerOptions.length; j++) {
      layerClickEventHandlerOn(
        options.map,
        options.layerOptions[j].id,
        MAP_CLICK_EVENT
      );
      options.layerOptions[j].mapClickEvent = MAP_CLICK_EVENT;
    }
  }
};

const _removeTileData = (options) => {
  removeLayer(options.map, options.layerId);
  removeSource(options.map, options.sourceId);
};

const isAcetateAdded = (map) => {
  let layers = map.getStyle().layers;
  for (let i = 0; i < layers.length; i++) {
    if (layers[i].id === LAYER_ID_ACETATE) {
      return true;
    }
  }
  return false;
};

// acetate layer is a blank layer used as a placeholder
// between OUR polygon (like pulse) layers and point layers
const _addAcetate = (map) => {
  let source = {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [],
    },
  };

  let layerOptions = {
    id: LAYER_ID_ACETATE,
    type: "fill",
    source: LAYER_ID_ACETATE,
  };

  addSource(map, LAYER_ID_ACETATE, source);
  addLayer(map, layerOptions);
};

const _fakeMapFeature = (feature) => {
  //Map stringifies everything that isn't a top level literal
  //this simulates that behavior
  let res = lodashCloneDeep(feature) || {};
  let props = res.properties || {};
  Object.keys(props).forEach(function (key, index) {
    if (typeof props[key] === "object") {
      props[key] = JSON.stringify(props[key]);
    }
  });
  res.props = {};
  return res;
};
